import { Flex } from '@chakra-ui/react';
import { Container, Text } from '@melio/penny';
import { FundingSource } from '@melio/platform-api';
import { useMelioIntl } from '@melio/platform-i18n';
import type { Plan, SubscriptionPreview } from '@melio/subscriptions-api';

import { usePlanInfo } from '../../../utils';
import { CardFundingSourceFormFields } from '../components/CardFundingSourceForm/types';
import { SubscriptionCheckoutSelection } from '../components/SubscriptionCheckoutSelection';
import { SubscriptionCheckoutSummary } from '../components/SubscriptionCheckoutSummary';

export type SubscriptionCheckoutScreenProps = {
  preview: SubscriptionPreview;
  plan: Plan;
  isPlanSaving: boolean;
  onSubmit: () => void;
  fundingSources?: FundingSource[];
  isLoading: boolean;
  newFundingSourceId?: string;
  onCreateCardDetailsDone: (fields: CardFundingSourceFormFields) => Promise<void>;
  onAddBankAccountClicked: VoidFunction;
};

export const SubscriptionCheckoutScreen = ({
  preview,
  plan,
  isPlanSaving,
  onSubmit,
  isLoading,
  fundingSources,
  onCreateCardDetailsDone,
  newFundingSourceId,
  onAddBankAccountClicked,
}: SubscriptionCheckoutScreenProps) => {
  const { formatMessage } = useMelioIntl();
  const { total, subTotal } = preview;
  const { planName } = usePlanInfo({ planId: plan.id });

  return (
    <Container
      paddingX="xxs"
      paddingY="xxs"
      data-testid="subscription-checkout-screen"
      data-component="SubscriptionCheckoutScreen"
    >
      <Text as="h1" textStyle="heading1Semi">
        {formatMessage('activities.subscription.checkout.title', { plan: planName })}
      </Text>

      <Flex justifyContent="space-between" marginTop="xl" gap="l" flexWrap={{ xs: 'wrap', m: 'nowrap' }}>
        <SubscriptionCheckoutSelection
          isPlanSaving={isPlanSaving}
          onAddBankAccountClicked={onAddBankAccountClicked}
          columns={4}
          fundingSources={fundingSources}
          newFundingSourceId={newFundingSourceId}
          isLoading={isLoading}
          onCreateCardDetailsDone={onCreateCardDetailsDone}
        />
        <SubscriptionCheckoutSummary
          columns={3}
          total={total}
          subTotal={subTotal}
          planName={planName}
          isPlanSaving={isPlanSaving}
          onSubmit={onSubmit}
        />
      </Flex>
    </Container>
  );
};
